import {
    DeleteOutlined,
    MenuOutlined,
    PlusOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
} from "@ant-design/icons";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SmlAsset, SmlJsonQueryOrder } from "@src/types/intelligence-search";
import { getMemberColor } from "@src/util/intelligence-search/get-member-info";
import { Button, Checkbox, Dropdown, List, Tooltip } from "antd";
import { uniqBy } from "lodash";
import React from "react";

export type OrderOption = {
    label: React.ReactNode;
    value: string;
    key: string;
    meta?: SmlAsset;
};
export type OrderSortableItemValue = SmlJsonQueryOrder & {
    meta?: SmlAsset;
};
export type SearchQueryOrderProps = {
    value?: SmlJsonQueryOrder[];
    onChange?: (value: SmlJsonQueryOrder[]) => void;
    options: OrderOption[];
};
export const SearchQueryOrder: React.FC<SearchQueryOrderProps> = ({ value = [], onChange, options }) => {
    const handleChange = (newValue: SmlJsonQueryOrder[]) => {
        onChange?.(newValue);
    };

    const updateDirection = (field: string, dir: "asc" | "desc") => {
        const newValue = value.map((item) => (item.name === field ? { ...item, direction: dir } : item));
        handleChange(newValue);
    };

    const removeOrderField = (field: string) => {
        const newValue = value.filter((item) => item.name !== field);
        handleChange(newValue);
    };

    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = value.findIndex((i) => i.name === active.id);
            const newIndex = value.findIndex((i) => i.name === over?.id);
            const newValue = arrayMove(value, oldIndex, newIndex);
            handleChange(newValue);
        }
    };
    const orderValue: OrderSortableItemValue[] = React.useMemo(() => {
        return value.map((item) => {
            const meta = options.find((opt) => opt.value === item.name)?.meta;
            return {
                ...item,
                meta: meta as SmlAsset,
            };
        });
    }, [value, options]);
    return (
        <div className="w-[300px] ">
            <OrderContentTitle options={options} value={value} onChange={handleChange} />
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext items={orderValue.map((item) => item.name)} strategy={verticalListSortingStrategy}>
                    <List
                        locale={{
                            emptyText: "Please add columns to configure order for records",
                        }}
                        className="min-h-[150px] max-h-[300px] overflow-y-scroll mt-4"
                        dataSource={orderValue}
                        renderItem={(item, index) => (
                            <OrderSortableItem
                                key={item.name}
                                id={item.name}
                                index={index}
                                item={item}
                                onChangeDirection={updateDirection}
                                onDelete={removeOrderField}
                            />
                        )}
                    />
                </SortableContext>
            </DndContext>
        </div>
    );
};

const OrderContentTitle: React.FC<{
    value: SmlJsonQueryOrder[];
    onChange: (value: SmlJsonQueryOrder[]) => void;
    options: OrderOption[];
}> = ({ value, onChange, options }) => {
    const handleAddOrderFields = (checkedValues: string[]) => {
        const oldOrder = value.filter((_order) => checkedValues.includes(_order.name));
        const newOrder: SmlJsonQueryOrder[] = options
            .filter((f) => checkedValues.includes(f.value))
            .map((opt) => ({
                name: opt?.value,
                direction: "asc",
            }));
        onChange(uniqBy([...oldOrder, ...newOrder], (item) => item.name));
    };

    return (
        <div className="flex  justify-between items-center">
            <span className="font-bold">Order</span>
            <Dropdown
                trigger={["click"]}
                placement="top"
                popupRender={() => {
                    return (
                        <div className="bg-white border-[1px] border-solid border-[#d9d9d9] p-3 rounded-lg min-w-[200px]">
                            <div className="max-h-[300px] overflow-y-scroll">
                                <Checkbox.Group
                                    value={value.map((v) => v.name)}
                                    onChange={(checkedValues) => {
                                        handleAddOrderFields(checkedValues);
                                    }}
                                >
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        {options.map((opt) => (
                                            <Checkbox key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </Checkbox>
                                        ))}
                                    </div>
                                </Checkbox.Group>
                            </div>
                        </div>
                    );
                }}
            >
                <Button type="dashed" icon={<PlusOutlined />}>
                    Add
                </Button>
            </Dropdown>
        </div>
    );
};

const OrderSortableItem: React.FC<{
    id: string;
    index: number;
    item: OrderSortableItemValue;
    onChangeDirection: (field: string, dir: "asc" | "desc") => void;
    onDelete: (field: string) => void;
}> = ({ id, item, onChangeDirection, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const itemMeta = item.meta;
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        background: isDragging ? "#f0f0f0" : "white",
        display: "flex",
        alignItems: "center",
        padding: 12,
        borderBottom: "1px solid #f0f0f0",
    };
    const toggleDirection = () => {
        const newDir = item.direction === "asc" ? "desc" : "asc";
        onChangeDirection(item.name, newDir);
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div {...listeners} className="cursor-grab mr-3">
                <MenuOutlined />
            </div>
            <div
                style={{
                    color: getMemberColor(itemMeta?.asset_type).textColor,
                }}
                className="flex-1 truncate"
            >
                {itemMeta?.title}
            </div>
            <Button
                size="small"
                type="text"
                icon={
                    item.direction === "asc" ? (
                        <Tooltip title="🔼 A–Z (asc)" destroyOnHidden mouseLeaveDelay={0}>
                            <SortAscendingOutlined className="text-[14px]" />
                        </Tooltip>
                    ) : (
                        <Tooltip title="🔽 Z–A (desc)" destroyOnHidden mouseLeaveDelay={0}>
                            <SortDescendingOutlined className="text-[14px]" />
                        </Tooltip>
                    )
                }
                onClick={toggleDirection}
                title={`Sort ${item.direction === "asc" ? "Ascending" : "Descending"}`}
            />
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onDelete(item.name)} />
        </div>
    );
};
